if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        if (!this.form) return;
        this.variantIdInput.disabled = false;
        this.onSubmitHandler = this.onSubmitHandler.bind(this);
        this.onSubmitButtonClick = this.onSubmitButtonClick.bind(this);
        this.form.addEventListener('submit', this.onSubmitHandler, true);
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        if (!this.submitButton) return;
        this.submitButtonText = this.submitButton.querySelector('span');
        this.submitButton.addEventListener('click', this.onSubmitButtonClick, true);

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.stripForeignSellingPlanFields();
        this.observeForeignSellingPlanFields();
      }

      onSubmitButtonClick(evt) {
        if (evt.button != null && evt.button !== 0) return;
        evt.preventDefault();
        evt.stopImmediatePropagation();
        this.onSubmitHandler(evt);
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        evt.stopPropagation();
        if (typeof evt.stopImmediatePropagation === 'function') evt.stopImmediatePropagation();
        if (this._adding) return;
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this._adding = true;

        this.handleErrorMessage();
        this.stripForeignSellingPlanFields();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.headers['Content-Type'] = 'application/json';

        const formData = new FormData(this.form);
        const packRoot = this.closest('product-info');
        const checkedPack = packRoot?.querySelector('.pack-size-card__input:checked');
        const selectedVariantId = Number(checkedPack?.value || checkedPack?.dataset?.variantId);
        if (selectedVariantId > 0) {
          formData.set('id', String(selectedVariantId));
        }
        formData.set('quantity', '1');
        formData.delete('properties[Pack Size]');
        formData.delete('properties[_pack_jars]');
        formData.delete('properties[_pack_units]');

        const purchaseMode = this.getPurchaseMode();
        const selectedId = selectedVariantId > 0 ? selectedVariantId : Number(formData.get('id'));
        const planId = purchaseMode === 'subscribe' ? this.resolveSellingPlanId(selectedId) : '';
        while (formData.has('selling_plan')) formData.delete('selling_plan');
        if (planId) {
          formData.set('selling_plan', String(planId));
        }

        if (this.cart) {
          formData.set(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id).join(',')
          );
          formData.set('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        const payload = this.formDataToPayload(formData);
        config.body = JSON.stringify(payload);

        const variantId = payload.id;
        const quantity = payload.quantity || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description || response.message || 'Unable to add this item to cart.');
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));
              this.error = false;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this._adding = false;
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner')?.classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      getPurchaseMode() {
        return (
          this.querySelector('input[data-purchase-mode]:checked')?.value ||
          this.closest('product-info')?.querySelector('input[data-purchase-mode]:checked')?.value ||
          this.dataset.purchaseMode ||
          'subscribe'
        );
      }

      getSellingPlanId() {
        const formId = this.form?.id;
        const associated = formId
          ? document.querySelector(`[data-selling-plan-input][form="${formId}"]`)
          : null;
        return (
          this.querySelector('[data-selling-plan-select]')?.value ||
          this.closest('product-info')?.querySelector('[data-selling-plan-select]')?.value ||
          this.querySelector('[data-selling-plan-input]')?.value ||
          associated?.value ||
          this.closest('product-info')?.querySelector('[data-selling-plan-input]')?.value ||
          this.dataset.sellingPlan ||
          ''
        );
      }

      planFrequencyKey(name) {
        const text = String(name || '').toLowerCase().replace(/\s+/g, ' ');
        if (/(2|two)\s*weeks?/.test(text)) return '2w';
        if (/(3|three)\s*months?/.test(text)) return '3m';
        if (/(2|two)\s*months?/.test(text)) return '2m';
        if (text.includes('week')) return '1w';
        if (text.includes('month')) return '1m';
        return text;
      }

      getVariantPlanAllocations(variantId) {
        const picker = this.closest('product-info')?.querySelector('[data-unit-pricing]');
        let data = null;
        try {
          data = JSON.parse(picker?.textContent || 'null');
        } catch (e) {
          data = null;
        }
        const variants = Array.isArray(data?.variants) ? data.variants : [];
        const variant =
          variants.find((item) => String(item.id) === String(variantId)) ||
          variants[0] ||
          null;
        const allocations = Array.isArray(variant?.selling_plan_allocations)
          ? variant.selling_plan_allocations
          : [];
        const appstle = allocations.filter((item) =>
          String(item.app_id || '').toLowerCase().includes('appstle')
        );
        if (appstle.length) return appstle;
        return allocations.filter((item) => !String(item.app_id || '').toLowerCase().includes('seal'));
      }

      resolveSellingPlanId(variantId) {
        const selectedId = String(this.getSellingPlanId() || '');
        const allocations = this.getVariantPlanAllocations(variantId);
        if (!allocations.length) return selectedId;

        if (selectedId && allocations.some((item) => String(item.selling_plan_id) === selectedId)) {
          return selectedId;
        }

        const select = this.closest('product-info')?.querySelector('[data-selling-plan-select]');
        const frequency = this.planFrequencyKey(select?.options[select.selectedIndex]?.textContent);
        const byFreq = allocations.find(
          (item) => this.planFrequencyKey(item.selling_plan_name) === frequency
        );
        return String(byFreq?.selling_plan_id || allocations[0]?.selling_plan_id || selectedId || '');
      }

      stripForeignSellingPlanFields() {
        const ours = [
          ...this.querySelectorAll('[data-selling-plan-input], [data-selling-plan-select]'),
          ...(this.closest('product-info')?.querySelectorAll('[data-selling-plan-input], [data-selling-plan-select]') || []),
        ];
        const formId = this.form?.id;
        const scope = this.closest('.custom-card, product-info, .product') || this;
        const fields = new Set([
          ...this.querySelectorAll('[name="selling_plan"]'),
          ...(formId ? document.querySelectorAll(`[name="selling_plan"][form="${formId}"]`) : []),
          ...scope.querySelectorAll(
            '.sealsubs-container input, .sealsubs-container select, .sealsubs-target-element input, .sealsubs-target-element select, [data-seal-extension] input, [data-seal-extension] select, .appstle_subscription_widget input, .appstle_subscription_widget select, .appstle_sub_widget input, .appstle_sub_widget select, [id^="appstle_subscription"] input, [id^="appstle_subscription"] select'
          ),
        ]);

        fields.forEach((field) => {
          if (ours.includes(field)) return;
          field.disabled = true;
          field.removeAttribute('name');
          field.removeAttribute('form');
        });
      }

      observeForeignSellingPlanFields() {
        if (this._sealObserver) return;
        const root = this.closest('.custom-card, product-info') || this;
        this._sealObserver = new MutationObserver(() => this.stripForeignSellingPlanFields());
        this._sealObserver.observe(root, { childList: true, subtree: true });
      }

      formDataToPayload(formData) {
        const payload = {};
        const properties = {};
        for (const [key, value] of formData.entries()) {
          if (key === 'form_type' || key === 'utf8' || key === 'add') continue;
          const propMatch = key.match(/^properties\[([^\]]+)\]$/);
          if (propMatch) {
            properties[propMatch[1]] = value;
            continue;
          }
          payload[key] = value;
        }
        if (Object.keys(properties).length) payload.properties = properties;
        if (payload.id != null && payload.id !== '') payload.id = Number(payload.id);
        if (payload.quantity != null && payload.quantity !== '') payload.quantity = Number(payload.quantity);
        if (payload.selling_plan) {
          payload.selling_plan = Number(payload.selling_plan);
        } else {
          delete payload.selling_plan;
        }
        return payload;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        const labelEl = this.submitButton.querySelector('[data-atc-label]') || this.submitButtonText;
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) labelEl.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          labelEl.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
